import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import {
  getErrorMessage,
  type PartFileUnits,
  useUpdatePartFile,
} from '@frontend/api';
import {
  Box,
  Flex,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@frontend/components';

import { PageTitle } from 'components/PageTitle';

import { MicroAppHeader } from 'containers/Layouts/DashboardLayout/MicroAppHeader';

import CADViewerInfo from 'modules/common/viewer/CADViewerInfo';
import CADViewerTools from 'modules/common/viewer/CADViewerTools';
import Viewer from 'modules/common/viewer/Viewer';
import { viewer } from 'modules/common/viewer/data/actions';
import {
  getActiveFile,
  getActiveFileProperties,
  getIsActiveFile3DRenderable,
} from 'modules/common/viewer/data/reducer';
import { ExtractedTableProvider } from 'modules/extractedTables/ExtractedTableContext';
import { ExtractedTableModalContainer } from 'modules/extractedTables/ExtractedTableModalContainer';
import { SelectedTableProvider } from 'modules/extractedTables/SelectedTableContext';

import { UnitSystemProvider } from 'utils/measurement/context';

import FileViewerCadTree from './components/FileViewerCadTree';
import { FileViewerHeader } from './components/FileViewerHeader';
import { UpdateItarModal } from './components/UpdateItarModal';
import { getActiveRootFileName } from './data/reducer';
import { PdfInteractionsProvider } from './hooks/usePdfInteractions';
import { useRefreshStaleQueriesOnMount } from './hooks/useRefreshStaleQueriesOnMount';

const FileViewerWrapped = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue(
    {
      base: true,
      tablet: true,
      desktop: false,
    },
    { ssr: false },
  );
  const dispatch = useDispatch();
  const activeFile = useSelector(getActiveFile);
  const rootFileName = useSelector(getActiveRootFileName);
  const activeFileProperties = useSelector(getActiveFileProperties);
  const isActiveFile3DRenderable = useSelector(getIsActiveFile3DRenderable);
  const shouldShowCAD = activeFile && activeFile.hasGeometry;
  const [cadViewerRegion, { height: cadViewerRegionHeight = 1 }] =
    useMeasure<HTMLDivElement>();

  const toast = useToast();
  const { mutate: updatePartFile } = useUpdatePartFile(
    { uuid: activeFile?.uuid ?? '' },
    {
      queryOptions: {
        onSuccess: (data) => {
          toast({
            status: 'success',
            title: 'Units updated',
          });
          if (activeFile) {
            // inject part file into the store with new units
            dispatch(
              viewer.receivePartFiles({
                partFiles: [
                  {
                    ...activeFile,
                    printUnits: data.printUnits,
                  },
                ],
              }),
            );
          }
        },
        onError: (error) => {
          toast({
            status: 'error',
            title: 'Failed to update units',
            description: getErrorMessage(error),
          });
        },
      },
    },
  );

  const handleUpdatePrintUnits = useCallback(
    (units: PartFileUnits) => {
      updatePartFile({ printUnits: units });
    },
    [updatePartFile],
  );

  useRefreshStaleQueriesOnMount();

  //Note: FileViewerCadTree receives parentHeight so it knows the maxHeight of the floating cad tree
  return (
    <Flex
      direction="column"
      overflow="hidden"
      position="fixed"
      height="100%"
      width="100%"
    >
      <ExtractedTableModalContainer />
      <PageTitle title={rootFileName} />
      <MicroAppHeader />
      <FileViewerHeader onOpen={onOpen} />
      <UpdateItarModal isOpen={isOpen} onClose={onClose} />
      <Flex height="100%" width="100%" overflowX="hidden">
        <Box
          background="linear-gradient(150deg, #fff 0%, #cccccc 100%)"
          flexGrow={1}
          overflow="hidden"
          position="relative"
          ref={cadViewerRegion}
        >
          {shouldShowCAD && (
            <>
              <FileViewerCadTree
                p={1.5}
                pb={isMobile ? 0 : undefined}
                w={isMobile ? '100%' : undefined}
                parentHeight={cadViewerRegionHeight}
              />
              <Box
                position="absolute"
                top={isMobile ? 7 : 0}
                zIndex={2}
                width="100%"
              >
                <CADViewerTools />
              </Box>
              <CADViewerInfo
                activeFileProperties={activeFileProperties}
                isActiveFile3DRenderable={isActiveFile3DRenderable}
              />
            </>
          )}
          <Viewer
            leftOffset={'300px'}
            isStandaloneViewer
            onUpdatePrintUnits={handleUpdatePrintUnits}
          />
        </Box>
      </Flex>
    </Flex>
  );
};

const FileViewer = () => {
  const activeFile = useSelector(getActiveFile);
  const partFileUuids = useMemo(
    () => (activeFile?.uuid ? [activeFile.uuid] : []),
    [activeFile],
  );
  return (
    <UnitSystemProvider>
      <ExtractedTableProvider partFileUuids={partFileUuids}>
        <SelectedTableProvider>
          <PdfInteractionsProvider>
            <FileViewerWrapped />
          </PdfInteractionsProvider>
        </SelectedTableProvider>
      </ExtractedTableProvider>
    </UnitSystemProvider>
  );
};

export default FileViewer;
